import React from 'react'
import { useEasingCurveData } from '../../hooks/useEasingCurveData'
import type { TweenConfig } from '../../types'

interface EasingCurveVisualizerProps {
  config: TweenConfig
  progress: number
}

const PAD = { top: 12, right: 12, bottom: 20, left: 30 }
const W = 288
const H = 150
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom

export function EasingCurveVisualizer({ config, progress }: EasingCurveVisualizerProps) {
  const { points, maxValue, minValue } = useEasingCurveData(config.ease)

  const yMin = Math.min(0, minValue - 0.05)
  const yMax = Math.max(1, maxValue + 0.05)
  const yRange = yMax - yMin

  const toX = (t: number) => PAD.left + t * PLOT_W
  const toY = (v: number) => PAD.top + (1 - (v - yMin) / yRange) * PLOT_H

  // Build SVG path
  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p.t).toFixed(1)},${toY(p.value).toFixed(1)}`)
    .join(' ')

  // Diagonal reference line (linear)
  const diagX1 = toX(0), diagY1 = toY(0)
  const diagX2 = toX(1), diagY2 = toY(1)

  // Playback dot position
  const dotIdx = Math.min(Math.floor(progress * (points.length - 1)), points.length - 1)
  const dotPoint = points[dotIdx]

  // Duration label
  const durationLabel = `${config.duration}s`

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Easing curve visualization">
      {/* Background */}
      <rect x="0.5" y="0.5" width={W - 1} height={H - 1} rx="6" fill="#f9fafb" stroke="#e5e7eb" />

      {/* Diagonal reference line (linear) */}
      <line
        x1={diagX1} y1={diagY1}
        x2={diagX2} y2={diagY2}
        stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 3"
      />

      {/* Boundary lines at y=0 and y=1 */}
      <line
        x1={PAD.left} y1={toY(1)}
        x2={W - PAD.right} y2={toY(1)}
        stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2 2"
      />
      <line
        x1={PAD.left} y1={toY(0)}
        x2={W - PAD.right} y2={toY(0)}
        stroke="#e5e7eb" strokeWidth="0.5" strokeDasharray="2 2"
      />

      {/* Y-axis labels */}
      <text x={PAD.left - 4} y={toY(1) + 1} textAnchor="end" className="text-[9px]" fill="#9ca3af" dominantBaseline="middle">1</text>
      <text x={PAD.left - 4} y={toY(0) + 1} textAnchor="end" className="text-[9px]" fill="#9ca3af" dominantBaseline="middle">0</text>

      {/* Easing curve */}
      <path d={pathD} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

      {/* Easing name label */}
      <text
        x={PAD.left + 4}
        y={PAD.top + 10}
        textAnchor="start"
        className="text-[9px] font-medium"
        fill="#6366f1"
      >
        {config.ease}
      </text>

      {/* Duration label */}
      <text
        x={W - PAD.right}
        y={H - 4}
        textAnchor="end"
        className="text-[9px]"
        fill="#9ca3af"
      >
        {durationLabel}
      </text>

      {/* Playback dot */}
      {progress > 0 && dotPoint && (
        <circle
          cx={toX(dotPoint.t)}
          cy={toY(dotPoint.value)}
          r="4"
          fill="#6366f1"
          stroke="white"
          strokeWidth="1.5"
        />
      )}
    </svg>
  )
}
